// Shared by Settings → Organisation, the first-run setup and the demo data:
// VAT periods with a stagger, as ledger._vat_period, and the financial year.
import type { OrganisationSettings } from '../../data/source';

export type VatFrequency = OrganisationSettings['vat_frequency'];
export type OrgPatch = Partial<{
  legal_name: string; trading_name: string | null; entity_type: OrganisationSettings['entity_type'];
  registered_number: string | null; registered_office: string | null; address: string | null;
  contact_email: string | null; contact_phone: string | null; website: string | null;
  base_currency: string; vat_registered: boolean; vat_number: string | null; vat_basis: OrganisationSettings['vat_basis'];
  vat_frequency: VatFrequency; vat_period_start_month: number; year_start_month: number; first_year: number; setup_completed: boolean;
  contact_codes: Partial<{ mode: 'auto' | 'manual'; customer_prefix: string; supplier_prefix: string; digits: number }>;
}>;

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT = MONTHS.map((m) => m.slice(0, 3));

export const FREQUENCIES: { value: VatFrequency; label: string; months: number }[] = [
  { value: 'monthly', label: 'Every month', months: 1 },
  { value: 'bimonthly', label: 'Every two months', months: 2 },
  { value: 'quarterly', label: 'Every three months (quarterly)', months: 3 },
  { value: 'four_monthly', label: 'Every four months', months: 4 },
  { value: 'biannual', label: 'Every six months', months: 6 },
  { value: 'annual', label: 'Once a year', months: 12 },
];
export const monthsIn = (f: VatFrequency) => FREQUENCIES.find((x) => x.value === f)?.months ?? 2;

const pad = (n: number) => String(n).padStart(2, '0');
function addMonths(y: number, m: number, n: number): { y: number; m: number } {
  const t = y * 12 + (m - 1) + n;
  return { y: Math.floor(t / 12), m: (t % 12) + 1 };
}
function lastDay(y: number, m: number) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }

/** The VAT period a date (yyyy-mm-dd) falls in, for a frequency and the month cycles start in. */
export function vatPeriod(date: string, freq: VatFrequency, startMonth: number): { from: string; to: string; label: string } {
  const n = monthsIn(freq);
  const y = Number(date.slice(0, 4)), m = Number(date.slice(5, 7));
  const back = (((m - startMonth) % n) + n) % n;
  const f = addMonths(y, m, -back);
  const t = addMonths(f.y, f.m, n - 1);
  const label = n === 1 ? `${SHORT[f.m - 1]} ${f.y}` : `${SHORT[f.m - 1]}–${SHORT[t.m - 1]}`;
  return { from: `${f.y}-${pad(f.m)}-01`, to: `${t.y}-${pad(t.m)}-${pad(lastDay(t.y, t.m))}`, label };
}

/** The cycle of periods through a year, for showing what a choice means: "Feb–Apr, May–Jul, Aug–Oct, Nov–Jan". */
export function periodCycle(freq: VatFrequency, startMonth: number): string {
  const n = monthsIn(freq);
  if (n === 1) return 'One return for each month';
  if (n === 12) return `${MONTHS[startMonth - 1]} to ${MONTHS[(startMonth + 10) % 12]}`;
  const out: string[] = [];
  for (let i = 0; i < 12 / n; i++) {
    const s = ((startMonth - 1 + i * n) % 12);
    out.push(`${SHORT[s]}–${SHORT[(s + n - 1) % 12]}`);
  }
  return out.join(', ');
}

/** Whether a new frequency and stagger follow on from the last return filed (to yyyy-mm-dd). */
export function followsOn(lastFiledTo: string | null | undefined, freq: VatFrequency, startMonth: number): boolean {
  if (!lastFiledTo) return true;
  const d = new Date(`${lastFiledTo}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  const next = d.toISOString().slice(0, 10);
  return vatPeriod(next, freq, startMonth).from === next;
}

/** The first financial year: FY label is the calendar year it ends in. */
export function firstYearRange(startMonth: number, firstYear: number): { from: string; to: string } {
  const startYear = startMonth === 1 ? firstYear : firstYear - 1;
  const end = addMonths(startYear, startMonth, 11);
  return { from: `${startYear}-${pad(startMonth)}-01`, to: `${end.y}-${pad(end.m)}-${pad(lastDay(end.y, end.m))}` };
}

/** Irish and UK VAT number checks, as ledger.update_organisation; returns the tidied number or an error. */
export function checkVatNumber(raw: string, jurisdiction: 'IE' | 'UK' | 'NI'): { value: string | null; error?: string } {
  let v = raw.replace(/[\s.-]/g, '').toUpperCase();
  if (!v) return { value: null };
  if (jurisdiction === 'IE') {
    if (!/^(IE)?[0-9]{7}[A-W][A-IW]?$/.test(v) && !/^(IE)?[0-9][A-Z+*][0-9]{5}[A-W]$/.test(v))
      return { value: null, error: 'An Irish VAT number is IE then 7 digits and one or two letters, such as IE1234567T' };
    if (!v.startsWith('IE')) v = `IE${v}`;
  } else {
    if (!/^(GB|XI)?([0-9]{9}|[0-9]{12})$/.test(v)) return { value: null, error: 'A UK VAT number is GB then 9 digits, such as GB123456789' };
    if (!/^(GB|XI)/.test(v)) v = `${jurisdiction === 'NI' ? 'XI' : 'GB'}${v}`;
  }
  return { value: v };
}
